import { UNAVAILABLE_IMAGE_URL } from 'common/Constants';
import { BrowseByTagsType } from 'common/events/ClientEvents';
import {
  BrowseFilter,
  CategoryPageContent,
  ConsumerExploreFoodItemFragment,
  DishFirstExploreSegmentShefFragment,
  DishFirstFoodItemsSegmentFragment,
  DishFirstSegmentFilter,
} from 'src/gqlReactTypings.generated.d';
import { isCategoryPageContent } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstShefsCardRow';
import { QueryParamsUpdates, QueryParamsUpdateType, UseExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { match } from 'ts-pattern';
import { CategoryCollection } from '../BrowseBy/BrowseByTags/Categories/CategoryCollection';
import { CategorySubcuisine } from '../BrowseBy/BrowseByTags/Categories/CategorySubcuisine';
import { Category } from '../BrowseBy/BrowseByTags/Categories/types';
import { isExploreSegmentShef } from './utils';

type SegmentContent = DishFirstFoodItemsSegmentFragment | JSX.Element;

const MAX_INDIVIDUAL_SUBCUISINE_SEGMENTS = 4;

// An incredibly brittle and hacky way for us to figure out what subcuisine segments are by excluding
// all segment filters that are not cuisine filters.
const cuisineSegments: Set<string> = new Set([
  DishFirstSegmentFilter.African,
  DishFirstSegmentFilter.American,
  DishFirstSegmentFilter.Bangladeshi,
  DishFirstSegmentFilter.Caribbean,
  DishFirstSegmentFilter.Chinese,
  DishFirstSegmentFilter.French,
  DishFirstSegmentFilter.Hungarian,
  DishFirstSegmentFilter.Indian,
  DishFirstSegmentFilter.Italian,
  DishFirstSegmentFilter.Japanese,
  DishFirstSegmentFilter.KidFriendly,
  DishFirstSegmentFilter.Korean,
  DishFirstSegmentFilter.LatinAmerican,
  DishFirstSegmentFilter.Mediterranean,
  DishFirstSegmentFilter.Mexican,
  DishFirstSegmentFilter.MiddleEastern,
  DishFirstSegmentFilter.Nepalese,
  DishFirstSegmentFilter.Pakistani,
  DishFirstSegmentFilter.Russian,
  DishFirstSegmentFilter.SoutheastAsian,
  DishFirstSegmentFilter.Southern,
  DishFirstSegmentFilter.Specialty,
]);

const nonCuisineSegments: string[] = Object.values(DishFirstSegmentFilter).filter(
  (value) => !cuisineSegments.has(value)
);

// See https://github.com/shefhq/shef-mono/pull/11391 for why we do this
const staffPicksShefSegmentId = `${DishFirstSegmentFilter.StaffPicks}_SHEFS`;

// Segments that should be aggregated
const collectionCategories: Set<string> = new Set([
  DishFirstSegmentFilter.Professional,
  DishFirstSegmentFilter.NewShefs,
  DishFirstSegmentFilter.Promos,
  staffPicksShefSegmentId,
]);

// eslint-disable-next-line no-underscore-dangle -- Apollo.
const isFoodItem = (item): item is ConsumerExploreFoodItemFragment => item.__typename === 'FoodItem';

const convertShefToCategory = (
  segment: DishFirstFoodItemsSegmentFragment,
  item: DishFirstExploreSegmentShefFragment,
  tagType: BrowseByTagsType
): Category => ({
  value: segment.id === staffPicksShefSegmentId ? DishFirstSegmentFilter.StaffPicks : segment.id,
  image: item.dishPreviewImage ?? UNAVAILABLE_IMAGE_URL,
  label: segment.displayName,
  tagType,
});

const convertFoodItemToCategory = (
  segment: DishFirstFoodItemsSegmentFragment,
  item: ConsumerExploreFoodItemFragment,
  tagType: BrowseByTagsType
): Category => ({
  value: segment.id,
  image: item.imageUrl ?? UNAVAILABLE_IMAGE_URL,
  label: segment.displayName,
  tagType,
});

/**
 * We can't just insert these category segments in because they are generated based on
 * segment values and remove some segments from display =(
 */
export const consolidateContentIfNecessary = (
  filters: UseExploreUrl<DishFirstSegmentFilter>,
  items: (DishFirstFoodItemsSegmentFragment | JSX.Element)[],
  updateQueryParams: (updates: QueryParamsUpdates<DishFirstSegmentFilter>, updateType?: QueryParamsUpdateType) => void
) => {
  if (filters.segmentFilters.length) {
    return items;
  }

  const updatedContent: SegmentContent[] = [];
  let subcuisinesCategoryPageContent: CategoryPageContent | null = null;
  const subcuisines: Category[] = [];
  const collections: Category[] = [];

  // eslint-disable-next-line functional/no-let -- Counter.
  let cuisineCount = 0;

  // eslint-disable-next-line functional/no-let -- Tracking where to insert more subcuisines segment.
  let lastCuisineIndex = 0;
  items.forEach((item, index) => {
    match(item)
      .with({ __typename: 'ExploreFoodItemsSegment' }, (segment: DishFirstFoodItemsSegmentFragment) => {
        const contentItem = segment.content.items[0];

        // 'More subcuisines' is available only for Cuisine secondary page.
        if (filters.browseFilter === BrowseFilter.Cuisine) {
          if (
            !nonCuisineSegments.some((nonCuisineSegment) => nonCuisineSegment.startsWith(segment.id)) &&
            isExploreSegmentShef(contentItem)
          ) {
            cuisineCount++;

            if (cuisineCount > MAX_INDIVIDUAL_SUBCUISINE_SEGMENTS) {
              // eslint-disable-next-line functional/immutable-data -- Appending.
              subcuisines.push(convertShefToCategory(segment, contentItem, BrowseByTagsType.CUISINE));
              if (isCategoryPageContent(segment.categoryPageContent)) {
                subcuisinesCategoryPageContent = segment.categoryPageContent;
              }
              return;
            }
            lastCuisineIndex = index;
          }
        }

        // 'Browse shefs by category' is available on either secondary page.
        if (filters.browseFilter === BrowseFilter.Cuisine || filters.browseFilter === BrowseFilter.Dietary) {
          if (collectionCategories.has(segment.id)) {
            if (isExploreSegmentShef(contentItem)) {
              // eslint-disable-next-line functional/immutable-data -- Appending.
              collections.push(convertShefToCategory(segment, contentItem, BrowseByTagsType.COLLECTION));
              return;
            }

            if (isFoodItem(contentItem)) {
              // eslint-disable-next-line functional/immutable-data -- Appending.
              collections.push(convertFoodItemToCategory(segment, contentItem, BrowseByTagsType.COLLECTION));
              return;
            }
          }
        }

        // eslint-disable-next-line functional/immutable-data -- Unaltered segment, just append.
        updatedContent.push(segment);
      })
      .otherwise((node) =>
        // eslint-disable-next-line functional/immutable-data -- Some element, just append.
        updatedContent.push(node)
      );
  });

  if (subcuisines.length) {
    // eslint-disable-next-line functional/immutable-data -- Shoving in 'More subcuisines' segment after last cuisine.
    updatedContent.splice(
      lastCuisineIndex + 1,
      0,
      <CategorySubcuisine
        categories={subcuisines}
        categoryPageContent={subcuisinesCategoryPageContent}
        updateQueryParams={updateQueryParams}
      />
    );
  }

  if (collections.length) {
    // eslint-disable-next-line functional/immutable-data -- Add to end collections segment.
    updatedContent.push(<CategoryCollection categories={collections} updateQueryParams={updateQueryParams} />);
  }

  return updatedContent;
};
